import * as THREE from 'three';
import type { DinosaurSpecies } from '../../state/game-state';
import { SPECIES_CONFIG } from './dinosaur-types';

const sharedMaterials = new Map<number, THREE.MeshStandardMaterial>();

function getMaterial(color: number, emissive: number, emissiveIntensity: number): THREE.MeshStandardMaterial {
  const key = (color << 16) | (emissive << 8) | Math.floor(emissiveIntensity * 100);
  if (!sharedMaterials.has(key)) {
    sharedMaterials.set(
      key,
      new THREE.MeshStandardMaterial({
        color,
        emissive,
        emissiveIntensity,
        roughness: 0.65,
        metalness: 0,
      })
    );
  }
  return sharedMaterials.get(key)!;
}

function buildSilhouette(
  species: DinosaurSpecies,
  scale: number
): THREE.Group {
  const cfg = SPECIES_CONFIG[species];
  const group = new THREE.Group();
  const bodyMat = getMaterial(cfg.bodyColor, cfg.emissive, cfg.emissiveIntensity);
  const headMat = getMaterial(cfg.headColor, cfg.emissive, cfg.emissiveIntensity);

  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.55, 1.4),
    bodyMat
  );
  torso.position.y = 0.85 * cfg.heightMul;
  torso.castShadow = true;
  group.add(torso);

  const neckLen = cfg.silhouette === 'raptor' || cfg.silhouette === 'large' ? 0.55 : 0.4;
  const neck = new THREE.Mesh(
    new THREE.BoxGeometry(0.28, neckLen, 0.28),
    bodyMat
  );
  neck.position.set(0, 1.2 * cfg.heightMul, -0.7);
  neck.rotation.x = -0.4;
  neck.castShadow = true;
  group.add(neck);

  const headSize = cfg.silhouette === 'large' ? 0.55 : cfg.silhouette === 'small' ? 0.18 : 0.36;
  const head = new THREE.Mesh(
    new THREE.BoxGeometry(headSize, headSize * 0.7, headSize * 1.2),
    headMat
  );
  head.position.set(0, 1.55 * cfg.heightMul, -1.0);
  head.castShadow = true;
  group.add(head);

  if (cfg.silhouette === 'frilled') {
    const frill = new THREE.Mesh(
      new THREE.RingGeometry(0.35, 0.6, 24, 1),
      new THREE.MeshStandardMaterial({
        color: 0xffcc00,
        side: THREE.DoubleSide,
        emissive: 0xffcc00,
        emissiveIntensity: 0.6,
        roughness: 0.5,
      })
    );
    frill.position.set(0, 1.55 * cfg.heightMul, -0.95);
    frill.rotation.y = Math.PI;
    group.add(frill);
  }

  if (cfg.silhouette === 'sail') {
    const sailShape = new THREE.Shape();
    sailShape.moveTo(0, 0);
    sailShape.lineTo(0.05, 0.8);
    sailShape.lineTo(0.5, 1.0);
    sailShape.lineTo(0.6, 0.5);
    sailShape.lineTo(0.5, 0);
    sailShape.lineTo(0, 0);
    const sailGeo = new THREE.ExtrudeGeometry(sailShape, { depth: 0.05, bevelEnabled: false });
    const sail = new THREE.Mesh(
      sailGeo,
      new THREE.MeshStandardMaterial({
        color: 0x3a2a1a,
        emissive: 0x3a2a1a,
        emissiveIntensity: 0.1,
        roughness: 0.7,
      })
    );
    sail.position.set(0, 0.85 * cfg.heightMul, 0.3);
    sail.rotation.y = Math.PI / 2;
    group.add(sail);
  }

  const tail = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.24, 1.1),
    bodyMat
  );
  tail.position.set(0, 1.0 * cfg.heightMul, 1.0);
  tail.rotation.x = 0.2;
  tail.castShadow = true;
  group.add(tail);

  if (cfg.silhouette === 'armored') {
    const armorMat = new THREE.MeshStandardMaterial({
      color: 0x5a6878,
      emissive: 0x202830,
      emissiveIntensity: 0.3,
      roughness: 0.4,
      metalness: 0.6,
    });
    for (let i = 0; i < 4; i++) {
      const plate = new THREE.Mesh(
        new THREE.BoxGeometry(0.55, 0.18, 0.4),
        armorMat
      );
      plate.position.set(0, 0.95 * cfg.heightMul, -0.5 + i * 0.4);
      plate.castShadow = true;
      group.add(plate);
    }
    const club = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 8, 6),
      armorMat
    );
    club.position.set(0, 0.85 * cfg.heightMul, 1.6);
    club.scale.set(1, 0.8, 1.2);
    club.castShadow = true;
    group.add(club);
  }

  const legCount = cfg.gait === 'quadruped' ? 4 : 2;
  if (legCount === 4) {
    for (const side of [-1, 1]) {
      for (const zPos of [0.3, -0.3]) {
        const leg = new THREE.Mesh(
          new THREE.BoxGeometry(0.16, 0.7 * cfg.heightMul, 0.16),
          bodyMat
        );
        leg.position.set(side * 0.18, 0.35 * cfg.heightMul, zPos);
        leg.castShadow = true;
        leg.name = `leg-${side > 0 ? 'r' : 'l'}-${zPos > 0 ? 'f' : 'b'}`;
        group.add(leg);
      }
    }
  } else {
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(
        new THREE.BoxGeometry(0.16, 0.7 * cfg.heightMul, 0.16),
        bodyMat
      );
      leg.position.set(side * 0.18, 0.35 * cfg.heightMul, -0.3);
      leg.castShadow = true;
      leg.name = `leg-${side > 0 ? 'r' : 'l'}-back`;
      group.add(leg);
    }
  }

  if (cfg.silhouette === 'raptor' || cfg.silhouette === 'frilled') {
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1;
      const arm = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.55, 0.1),
        bodyMat
      );
      arm.position.set(side * 0.42, 1.15 * cfg.heightMul, -0.55);
      arm.castShadow = true;
      arm.name = `arm-${i}`;
      group.add(arm);
    }
  }

  const eyeMat = new THREE.MeshBasicMaterial({ color: 0xff2222 });
  for (let i = 0; i < 2; i++) {
    const side = i === 0 ? -1 : 1;
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(0.04, 6, 6),
      eyeMat
    );
    eye.position.set(side * 0.12, 1.6 * cfg.heightMul, -1.2 * (headSize / 0.36));
    group.add(eye);
  }

  group.scale.setScalar(scale);
  return group;
}

export function buildDinosaur(species: DinosaurSpecies, scale: number): THREE.Group {
  return buildSilhouette(species, scale);
}
